/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/** Normalizes PostgreSQL timestamps to ISO-8601 for cross-browser Date parsing. */
export function normalizeDbTimestamp(raw: string): string {
  return raw
    .trim()
    .replace(' ', 'T')
    .replace(
      /(T\d{2}:\d{2}:\d{2})([+-]\d{2})(?::?(\d{2}))?$/,
      (_m, time: string, hours: string, minutes?: string) => `${time}${hours}:${minutes ?? '00'}`,
    );
}

/** Parses a DB timestamp to epoch millis, or `null` when absent or unparseable. */
export function parseDbTimestamp(raw?: string): number | null {
  if (!raw) return null;
  const t = Date.parse(normalizeDbTimestamp(raw));
  return Number.isNaN(t) ? null : t;
}

/**
 * A DB timestamp as the technician reads it, or `null` when absent or unparseable.
 *
 * Rendered in UTC because these stamps are dates rather than instants: read locally, a date-only
 * stamp lands a day early for anyone west of Greenwich.
 */
export function shortDbDate(raw: string | undefined, locale: string): string | null {
  const at = parseDbTimestamp(raw);
  if (at === null) return null;
  return new Date(at).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Short relative label (e.g. "3h ago") from a DB timestamp; empty string when unavailable. */
export function formatRelativeTime(raw?: string): string {
  const t = parseDbTimestamp(raw);
  if (t == null) return '';
  const min = Math.floor((Date.now() - t) / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const wk = Math.floor(day / 7);
  if (wk < 5) return `${wk}w ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(day / 365)}y ago`;
}
